import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDocumentByUUID } from "../../../../api/documentsService";
import { updateStatusForTimeline } from "../../../../api/timelineService";
import useCustomToast from "../../../../customHooks/useCustomToast";
import { setDocumentsFromApiCall } from "../../../../Redux/documentsSlice";
import {
  cancelUpload,
  initiateCancelUploadProcess,
  keepUploading,
  retryUpload,
} from "../../../../utils/cancelFileUpload";
import { uploadFileWithProgress } from "../../../../utils/UTILITY_METHODS";
import FileUpload from "../../../FileUpload";
import CancelFileUploadModal from "../../../Modals/CancelFileUploadModal";
import FillBureauDetailsModa from "../../../Modals/FillBureauDetailsModa";
import UploadProgress from "../../../UploadProgress";
import SingleBureauDocument from "./SingleBureauDocument";

const BureauDocuments = ({ handleUserTabClicked }) => {
  const { bureauData } = useSelector((store) => store.documentsDetails);
  const [bureauUploadSuccess, setBureauUploadSuccess] = useState(false);
  const [bureauUploadProgress, setBureauUploadProgress] = useState(0);
  const [isBureauUploading, setIsBureauUploading] = useState(false);
  const [bureauUploadFailed, setBureauUploadFailed] = useState(false);
  const [bureauDetailFile, setBureauDetailFile] = useState(null);
  const [lastBureauFile, setLastBureauFile] = useState(null);
  const [formValues, setFormValues] = useState({
    panNumber: "",
  });
  const showToast = useCustomToast();
  const dispatch = useDispatch();

  // Create a ref to store the AbortController
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);

  // State for the  cancel file upload confirmation modal
  const {
    isOpen: isCancelFileUploadModalOpen,
    onOpen: openCancelFileUploadModal,
    onClose: closeCancelFileUploadModal,
  } = useDisclosure();

  //state to open modal
  const {
    isOpen: isBureauDetailsModalOpen,
    onOpen: openBureauDetailsModal,
    onClose: closeBureauDetailsModal,
  } = useDisclosure();

  const handleBureauUpload = async (files) => {
    const file = files[0];
    setLastBureauFile(file); // Store file for retry
    setBureauUploadFailed(false); // Reset failure state
    setIsBureauUploading(true); // Show uploading state

    // Initialize a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const data = await uploadFileWithProgress(
        file,
        setBureauUploadProgress,
        setBureauUploadFailed,
        setBureauUploadSuccess,
        {
          documentType: "bureau",
          documentSubType: "bureau",
          documentNumber: formValues.panNumber, // this is pan value entered from modal
          documentNumberIdentifier: "pan_number",
          level: "customer",
        },
        abortController.signal // Pass the signal
      );
      setIsBureauUploading(false);
      setBureauUploadProgress(0);
      setFormValues({
        bureauDetailFile,
      });
      showToast(data.message, "success");
      if (isCancelFileUploadModalOpen) closeCancelFileUploadModal();
      const bureauDocumentUUID = data.data;
      const DocumentDetails = await getDocumentByUUID(bureauDocumentUUID);
      dispatch(setDocumentsFromApiCall([DocumentDetails.data]));
      abortControllerRef.current = null; // Reset the controller
    } catch (error) {
      if (error.message === "Upload canceled") {
        showToast("Upload canceled", "warning");
      } else {
        showToast(error.message || "An error occurred during upload", "error");
      }
      setIsBureauUploading(false);
      abortControllerRef.current = null; // Reset the controller
    }
  };

  // Function to trigger the hidden input click
  const handleInputClick = () => {
    inputRef.current.click(); // This triggers the hidden input element
  };

  // Handle form submission (Bureau Details Modal)
  const handleFormSubmit = () => {
    if (formValues.panNumber.trim()) {
      closeBureauDetailsModal(); // Close the modal after successful submission
      if (bureauDetailFile) {
        const file = bureauDetailFile;
        handleBureauUpload(file);
        setBureauDetailFile(null);
      } else {
        handleInputClick();
      }
    } else {
      showToast("All fields are required.", "warning");
    }
  };

  // Reset modal and form state on cancel
  const handleBureauDetailsModalCancel = () => {
    setFormValues({ panNumber: "" });
    setBureauUploadProgress(0);
    closeBureauDetailsModal();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      minHeight="100px" // Ensure there's a minimum height to avoid content squishing
      maxHeight="auto" // Allow the component to grow as necessary
    >
      <Box>
        <Heading as="h2" size="md">
          Bureau Data Information
        </Heading>
        <Text color={"gray.600"} fontSize="sm" mt={2}>
          Enter your PAN number to fetch your credit bureau report
          automatically. Click on 'Fetch Report' to retrieve the data from our
          external partner.
        </Text>

        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={2}
          mt={5}
          onClick={() => {
            openBureauDetailsModal();
          }}
        >
          <FileUpload
            onFileUpload={handleBureauUpload}
            width={"100%"}
            isUploading={isBureauUploading}
            uploadFailed={bureauUploadFailed}
            uploadSuccess={bureauUploadSuccess}
            isIdentificationDocument={false}
            inputRef={inputRef}
            setFillDetailsModalOpen={openBureauDetailsModal}
            setFileToUpload={setBureauDetailFile}
            acceptImageOrPdf={false}
            acceptOnlyPdf={true}
          />
        </Stack>

        <FillBureauDetailsModa
          isBureauDetailsModalOpen={isBureauDetailsModalOpen}
          closeBureauDetailsModal={closeBureauDetailsModal}
          setFormValues={setFormValues}
          formValues={formValues}
          handleFormSubmit={handleFormSubmit}
          handleBureauDetailsModalCancel={handleBureauDetailsModalCancel}
        />

        {/* Bureau Document Upload Progress */}
        {isBureauUploading && (
          <UploadProgress
            documentType="Bureau Document"
            isUploading={isBureauUploading}
            uploadProgress={bureauUploadProgress}
            uploadFailed={bureauUploadFailed}
            initiateCancelUploadProcess={() =>
              initiateCancelUploadProcess(
                abortControllerRef,
                openCancelFileUploadModal
              )
            }
            onRetry={() =>
              retryUpload(
                bureauUploadFailed,
                lastBureauFile,
                handleBureauUpload
              )
            }
          />
        )}
      </Box>

      {/* Cancel Upload Confirmation Modal */}
      <CancelFileUploadModal
        isModalOpen={isCancelFileUploadModalOpen}
        closeModal={closeCancelFileUploadModal}
        cancelUpload={() =>
          cancelUpload(
            abortControllerRef,
            setIsBureauUploading,
            setBureauUploadProgress,
            closeCancelFileUploadModal
          )
        }
        keepUploading={() => keepUploading(closeCancelFileUploadModal)}
      />

      <Stack spacing={3} mt={5}>
        {bureauData.map((document, index) => (
          <SingleBureauDocument
            key={document.uuid || index}
            name={
              document?.original_filename.length > 20
                ? `${document?.original_filename.substring(0, 20)}...`
                : document?.original_filename
            }
            isLast={index === bureauData.length - 1}
            scannedCopyDetected={index === 2}
            detectedMessage={"Scanned Copy Detected"}
          />
        ))}
      </Stack>

      <Box display={"flex"} justifyContent={"flex-end"} columnGap={4} mt={5}>
        <Button
          colorScheme="blackAlpha"
          color={"blackAlpha.700"}
          size="sm"
          variant="outline"
          px={12}
          onClick={() => handleUserTabClicked(3)}
        >
          Back
        </Button>
        <Button
          colorScheme={!Boolean(bureauData.length) ? "blackAlpha" : "teal"}
          color={!Boolean(bureauData.length) ? "blackAlpha" : "white"}
          size="sm"
          variant="solid"
          px={12}
          disabled={!Boolean(bureauData.length)}
          onClick={() => {
            handleUserTabClicked(-1);
            //update user timeline on server
            updateStatusForTimeline(
              5,
              "financial_information",
              "bureau_data",
              "completed"
            );
          }} // This means user has completed all the steps
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default BureauDocuments;
